import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { tokenStorage, setAuthErrorHandler } from '../config/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Register auth error handler so 401 responses trigger logout
  useEffect(() => {
    setAuthErrorHandler(() => {
      console.warn('[AuthContext] Auth error handler triggered — logging out');
      setUser(null);
    });
  }, []);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const token = await tokenStorage.getToken();
      const userData = await tokenStorage.getUserData();

      console.log('[AuthContext] checkAuthState:', token ? `token=${token.slice(0,20)}...` : 'no token', userData ? `user=${userData.email} role=${userData.role}` : 'no user');

      if (token && userData) {
        // Clear stale DRIVER sessions — rider app only allows RIDER/CORPORATE_ADMIN
        if (userData.role === 'DRIVER') {
          console.warn('[AuthContext] Clearing stale DRIVER session for:', userData.email);
          await tokenStorage.removeToken();
          await tokenStorage.removeUserData();
        } else {
          setUser(userData);
        }
      }
    } catch (error) {
      console.error('[AuthContext] Error checking auth state:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('[AuthContext] Attempting login for:', email);
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;

        console.log('[AuthContext] Login success, storing token length:', token?.length, 'role:', userData?.role);

        // Block DRIVER accounts from using the rider app
        if (userData?.role === 'DRIVER') {
          console.warn('[AuthContext] DRIVER account attempted rider login:', userData.email);
          return {
            success: false,
            message: 'This account is registered as a driver. Please use a different email to create a rider account.',
          };
        }

        // Store token and user data securely
        await tokenStorage.setToken(token);
        await tokenStorage.setUserData(userData);

        setUser(userData);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('[AuthContext] Login error:', error.response?.data?.message || error.message);
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  const register = async (name, email, password) => {
    try {
      console.log('[AuthContext] Attempting registration for:', email);
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        role: 'RIDER',
      });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;

        console.log('[AuthContext] Registration success, storing token length:', token?.length, 'role:', userData?.role);

        await tokenStorage.setToken(token);
        await tokenStorage.setUserData(userData);

        setUser(userData);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('[AuthContext] Registration error:', error.response?.data?.message || error.message);
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      console.log('[AuthContext] Logging out');
      await tokenStorage.removeToken();
      await tokenStorage.removeUserData();
      setUser(null);
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
